
import { useState, useEffect } from "react";
import { Cliente, Gerador } from "@/types";
import { ClienteService, GeradorService } from "@/services/StorageService";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export const useClientes = () => {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [filteredClientes, setFilteredClientes] = useState<Cliente[]>([]);
  const [geradores, setGeradores] = useState<Record<string, Gerador>>({});
  const [search, setSearch] = useState("");
  const { user, isAdmin, canAccessResource } = useAuth();

  useEffect(() => {
    try {
      // Carrega clientes com base na permissão do usuário
      const clientesData = isAdmin() 
        ? ClienteService.getAll() 
        : ClienteService.getByUserId(user?.id || "");
      
      // Carrega geradores com base na permissão do usuário
      const geradoresData = isAdmin()
        ? GeradorService.getAll()
        : GeradorService.getByUserId(user?.id || "");
      
      const geradoresMap: Record<string, Gerador> = {};
      geradoresData.forEach(gerador => {
        geradoresMap[gerador.id] = gerador;
      });
      
      setClientes(clientesData);
      setFilteredClientes(clientesData);
      setGeradores(geradoresMap);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar os dados");
    }
  }, [user, isAdmin]);

  useEffect(() => {
    if (search) {
      const filtered = clientes.filter(cliente => 
        cliente.nome.toLowerCase().includes(search.toLowerCase()) ||
        cliente.cidade.toLowerCase().includes(search.toLowerCase()) ||
        cliente.estado.toLowerCase().includes(search.toLowerCase()) ||
        cliente.concessionaria.toLowerCase().includes(search.toLowerCase()) ||
        (cliente.email || "").toLowerCase().includes(search.toLowerCase()) ||
        (cliente.celular || "").toLowerCase().includes(search.toLowerCase()) ||
        (cliente.status || "").toLowerCase().includes(search.toLowerCase()) ||
        (geradores[cliente.geradorId]?.nomeTitular || "").toLowerCase().includes(search.toLowerCase())
      );
      setFilteredClientes(filtered);
    } else {
      setFilteredClientes(clientes);
    }
  }, [search, clientes, geradores]);

  const handleExport = () => {
    if (filteredClientes.length === 0) return;
    
    const headers = ['Nome', 'Documento', 'Email', 'Celular', 'Status', 'Cidade', 'Estado', 'Concessionária', 
                    'Nº Cliente', 'Nº Instalação', 'Gasto (R$)', 'Consumo (kWh)', 
                    'Economia (R$)', 'Economia (%)', 'Gerador'];
    
    const rows = filteredClientes.map(c => [
      c.nome,
      c.documento,
      c.email || "",
      c.celular || "",
      c.status || "Ativo",
      c.cidade,
      c.estado,
      c.concessionaria,
      c.numeroCliente,
      c.numeroInstalacao,
      c.gastoEnergia.toString(),
      c.consumoMedio?.toString() || '0',
      c.economiaEstimada?.toString() || '0',
      c.percentualEconomia?.toString() || '0',
      geradores[c.geradorId]?.nomeTitular || 'N/A'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'clientes.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Verifica se o usuário pode acessar um cliente específico
  const userCanAccessCliente = (cliente: Cliente) => {
    return canAccessResource(cliente.criadoPor);
  };

  // Verifica se o usuário pode acessar o gerador associado ao cliente
  const userCanAccessGerador = (geradorId: string) => {
    const gerador = geradores[geradorId];
    return gerador ? canAccessResource(gerador.criadoPor) : false;
  };

  return {
    filteredClientes,
    geradores,
    search,
    setSearch,
    handleExport,
    userCanAccessCliente,
    userCanAccessGerador,
    isAdmin
  };
};
